import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  alpha,
  useTheme
} from '@mui/material'
import {
  AccountCircle,
  CheckCircle,
  Business
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { apiClient, setCsrfToken } from '@shared/services/ApiClient'
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter'

const setupSchema = yup.object({
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(256, 'Email must be less than 256 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(15, 'Password must be at least 15 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  phoneNumber: yup.string().optional(),
  agreeToTerms: yup
    .boolean()
    .required('You must agree to the terms and conditions')
    .oneOf([true], 'You must agree to the terms and conditions')
})

interface SetupFormData {
  companyName: string
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phoneNumber?: string
  agreeToTerms: boolean
}

export default function MarketplaceSetup() {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const tenantId = searchParams.get('tenantId')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<SetupFormData>({
    resolver: yupResolver(setupSchema),
    mode: 'onBlur',
    defaultValues: {
      agreeToTerms: false
    }
  })

  const password = watch('password', '')

  const onSubmit = async (data: SetupFormData) => {
    if (!tenantId) {
      setError('Missing tenant information. Please contact support.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Register the admin user for the marketplace tenant
      const response = await apiClient.post<any>('/auth/register', {
        companyName: data.companyName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || null,
        tenantId: tenantId,
        role: 'Admin', // First user is always admin
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      })

      setSuccess(true)

      if (response.csrfToken) {
        setCsrfToken(response.csrfToken)
      }

      // Redirect to email verification page instead of dashboard
      // since email needs to be verified first
      setTimeout(() => {
        navigate('/verify-email', {
          state: {
            email: data.email,
            message: 'Account created! Please check your email to verify your account and complete setup.'
          }
        })
      }, 1500)
    } catch (err: any) {
      console.error('Marketplace setup error:', err)
      setError(
        err.message ||
        'Failed to create admin account. Please try again or contact support.'
      )
      setLoading(false)
    }
  }

  if (!tenantId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={8}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                Missing tenant information. Please contact support or try activating
                your subscription again.
              </Alert>
              <Button variant="contained" href="/">
                Go to Home Page
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={8}>
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mb: 2
                }}
              >
                <AccountCircle sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Create Admin Account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Set up your administrator account to access your new workspace
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                icon={<CheckCircle fontSize="large" />}
                sx={{ mb: 3 }}
              >
                <Typography variant="body1" fontWeight="medium">
                  Account created successfully! Redirecting to dashboard...
                </Typography>
              </Alert>
            )}

            {!success && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    {...register('companyName')}
                    error={!!errors.companyName}
                    helperText={errors.companyName?.message}
                    disabled={loading}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      {...register('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      disabled={loading}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      {...register('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      disabled={loading}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    type="email"
                    label="Email Address"
                    autoComplete="username email"
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />

                  <TextField
                    fullWidth
                    label="Phone Number (Optional)"
                    placeholder="+1 555 123 4567"
                    {...register('phoneNumber')}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    disabled={loading}
                  />

                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    autoComplete="new-password"
                    {...register('password')}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={loading}
                  />
                  <PasswordStrengthMeter password={password || ''} />

                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm Password"
                    autoComplete="new-password"
                    {...register('confirmPassword')}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    disabled={loading}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('agreeToTerms')}
                        disabled={loading}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{' '}
                        <MuiLink href="/terms" target="_blank" sx={{ cursor: 'pointer' }}>
                          Terms and Conditions
                        </MuiLink>{' '}
                        and{' '}
                        <MuiLink href="/privacy" target="_blank" sx={{ cursor: 'pointer' }}>
                          Privacy Policy
                        </MuiLink>
                      </Typography>
                    }
                  />
                  {errors.agreeToTerms && (
                    <Typography variant="caption" color="error" sx={{ mt: -2 }}>
                      {errors.agreeToTerms.message}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Account & Continue'}
                  </Button>
                </Box>
              </form>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                <Business sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                Subscription managed through Microsoft Azure Marketplace
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
